import {
  Alert,
  Button,
  Card,
  Details,
  Heading,
  Link,
  Paragraph,
  useMediaQuery,
} from "@digdir/designsystemet-react";
import { ChevronRightIcon, ExternalLinkIcon } from "@navikt/aksel-icons";
import type { PropsWithChildren } from "react";
import { Trans, useTranslation } from "react-i18next";
import { NyttigeLenker } from "@/components/NyttigeLenker";
import { useShouldShowPasswordProtection } from "@/components/PasswordProtection";
import { env } from "@/env";
import { useAuth } from "@/features/auth";
import { cn } from "@/utils";
import landingPageImg from "/fjernvarme-illu-web.webp";
import { GlobaltVarsel } from "../varsler/GlobaltVarsel";

type ErrorCodeProps = {
  errorCode?: string;
};

export function LandingPage({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  return (
    <div>
      <GlobaltVarsel />
      <main className="container mx-auto grid grid-cols-1 xl:grid-cols-[1fr_auto] py-14 px-10 gap-6 gap-x-12 max-w-page">
        <article className="col-span-1">
          <LandingHeading />
          <LandingImage className="xl:hidden" />
          <LandingInfo errorCode={errorCode} />
        </article>
        <div className="col-span-1">
          <LandingImage className="hidden xl:block mb-8" />
          <section className="order-2 col-span-2 lg:col-span-1 lg:order-2 w-fit">
            <NyttigeLenker />

            <Card className="p-6 w-full mt-6">
              <Heading level={2} data-size="xs" className="mb-4">
                {t("pages.kundestotte.fjernvarmeleverandoer.tittel")}
              </Heading>
              <Link
                href={env.appSelskapsportalUrl}
                target="_blank"
                rel="noreferrer"
              >
                <div className="flex justify-between">
                  <Paragraph>
                    {" "}
                    {t(
                      "pages.kundestotte.fjernvarmeleverandoer.lenke-kontakt-oss",
                    )}
                  </Paragraph>
                  <ChevronRightIcon fontSize={"2rem"} className="pl-2" />
                </div>
              </Link>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
}

function LandingHeading() {
  const { t } = useTranslation();
  const isLarge = useMediaQuery("(min-width: 1024px)");
  // TODO password: Fjern når passordbeskyttelse ikke er nødvendig lenger. Husk å fjerne komponenten og hooken også.
  const passwordProtection = useShouldShowPasswordProtection();
  return (
    <div className="flex flex-col gap-6 col-span-1">
      {passwordProtection.passwordIsRequired && (
        <div
          role="alert"
          className="flex flex-col gap-4 bg-balder-background-warning border border-balder-border-warning p-8 round rounded-2xl"
          data-color="warning"
        >
          <Heading level={2} data-size="xs">
            {t("pages.landing.ikke-aapnet-enda.tittel")}
          </Heading>
          <Paragraph>
            {t("pages.landing.ikke-aapnet-enda.beskrivelse")}
          </Paragraph>
          <Paragraph>
            <Link
              target="_blank"
              rel="noreferrer"
              href={t("eksterneLenker.merOmNorgespris.url")}
            >
              {t("pages.landing.ikke-aapnet-enda.url-tittel")}
              <ExternalLinkIcon fontSize={"2rem"} className="pl-2" />
            </Link>
          </Paragraph>
        </div>
      )}
      <Heading level={1} data-size={isLarge ? "xl" : "md"} className="mb-6">
        {t("app.tittel")}
      </Heading>
    </div>
  );
}

const UthevdComponent = ({ children }: PropsWithChildren) => (
  <strong className="font-semibold">
    <i>{children}</i>
  </strong>
);

function LandingInfo({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  // TODO password: Fjern når passordbeskyttelse ikke er nødvendig lenger. Husk å fjerne komponenten og hooken også.
  const passwordProtection = useShouldShowPasswordProtection();
  return (
    <div className="flex flex-col gap-6 col-span-2 xl:col-span-1">
      <section>
        <Paragraph data-size={"xl"}>{t("pages.landing.ingress")}</Paragraph>

        {errorCode && <FeilmeldingForInnlogging errorCode={errorCode} />}
      </section>

      {!passwordProtection.passwordIsRequired && <BestillButton />}
      <section className="mb-2">
        <Paragraph data-size={"xl"}>
          {t("pages.landing.paa-vegne-av.tittel")}
        </Paragraph>
        <Paragraph>
          <Trans
            i18nKey="pages.landing.paa-vegne-av.beskrivelse"
            components={{
              Uthevd: <UthevdComponent />,
            }}
          />
        </Paragraph>

        <Link
          href={t("pages.aktoer.altinnlenke.url")}
          target="_blank"
          rel="noreferrer"
        >
          {t("pages.aktoer.altinnlenke.tittel")}
          <ExternalLinkIcon fontSize={"2rem"} className="pl-2" />
        </Link>
      </section>
      <MerInfo />
    </div>
  );
}

export function FeilmeldingForInnlogging({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  if (!errorCode) {
    return;
  }
  return (
    <Alert data-color="danger" data-size="sm" className="mt-8">
      <Heading data-size="xs">
        {t("feilmelding.overskrift", { context: errorCode })}
      </Heading>
      <Paragraph>
        {t("feilmelding.beskrivelse", { context: errorCode })}
      </Paragraph>
    </Alert>
  );
}

type ClassNameProps = {
  className?: string;
};
function LandingImage({ className }: ClassNameProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "max-w-xl col-span-2 xl:col-span-1 mx-auto xl:mx-0",
        className,
      )}
    >
      <img
        src={landingPageImg}
        alt={t("pages.landing.hjelpetekst-bilde")}
        width="100%"
        className="w-full object-scale-down"
      />
    </div>
  );
}

function BestillButton({ className }: ClassNameProps) {
  const { t } = useTranslation();
  const { signinRedirect } = useAuth();

  return (
    <div
      className={cn(
        "col-span-2 flex justify-center xl:justify-start mt-6 mb-6",
        className,
      )}
    >
      <Button className="w-xs xl:w-max" onClick={signinRedirect}>
        {t("pages.landing.button-bestill")}
        <ChevronRightIcon fontSize="1.5rem" className="shrink-0" />
      </Button>
    </div>
  );
}

const InfoContainer = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => {
  return (
    <Details className="mb-0">
      <Details.Summary>{title}</Details.Summary>
      <Details.Content>
        <Paragraph className="text-pretty whitespace-pre-line">
          {content}
        </Paragraph>
      </Details.Content>
    </Details>
  );
};

function MerInfo() {
  const { t } = useTranslation();
  return (
    <div className="order-1 xl:order-1 col-span-2 xl:col-span-1">
      <Heading data-size="sm" className="mb-4">
        {t("pages.landing.mer-info.tittel")}
      </Heading>
      <Card className="space-y-4">
        <InfoContainer
          title={t("pages.landing.mer-info.prisregulering.tittel")}
          content={t("pages.landing.mer-info.prisregulering.beskrivelse")}
        />
        <InfoContainer
          title={t("pages.landing.mer-info.forbrukstak.tittel")}
          content={t("pages.landing.mer-info.forbrukstak.beskrivelse")}
        />
        <InfoContainer
          title={t("pages.landing.mer-info.norgespris.tittel")}
          content={t("pages.landing.mer-info.norgespris.beskrivelse")}
        />
        <InfoContainer
          title={t("pages.landing.mer-info.avtaleperiode.tittel")}
          content={t("pages.landing.mer-info.avtaleperiode.beskrivelse")}
        />
        <InfoContainer
          title={t("pages.landing.mer-info.gjelder.tittel")}
          content={t("pages.landing.mer-info.gjelder.beskrivelse")}
        />
        <InfoContainer
          title={t("pages.landing.mer-info.underlangt.tittel")}
          content={t("pages.landing.mer-info.underlangt.beskrivelse")}
        />
      </Card>
    </div>
  );
}
